import React, { useState, useMemo, useCallback, useRef } from 'react';
import CoordinateGrid, { GridPoint, GridSegment } from './CoordinateGrid';
import LessonHeader, { MasteryQuestion } from './LessonHeader';
import ExerciseFooter, { FooterState } from './ExerciseFooter';

// ============ TYPES ============

export type LessonNodeType = 'intro' | 'question' | 'explanatory' | 'outro' | 'skill-check-intro' | 'complete';

export type QuestionType = 'place-point' | 'separation';

export interface LessonNode {
  id: string;
  type: LessonNodeType;
  title?: string;
  subtitle?: string;
  text?: string;
  bottomText?: string;
  questionText?: string;
  questionType?: QuestionType;
  gridSize?: number;
  gridRange?: number;
  targetCoordinates?: { x: number; y: number };
  referencePoint?: { x: number; y: number };
  targetSeparation?: { horizontal: number; vertical: number };
  displayPoints?: GridPoint[];
  displaySegments?: GridSegment[];
  explanation?: string;
  xpReward?: number;
}

// ============ LESSON DATA ============

const LESSON_NODES: LessonNode[] = [
  // SCREEN 1: QUESTION 1 - Place point at (2, 1)
  {
    id: 'q1',
    type: 'question',
    questionText: 'Place a point at the coordinates (2, 1).',
    questionType: 'place-point',
    gridSize: 380,
    gridRange: 5,
    targetCoordinates: { x: 2, y: 1 },
    explanation: 'The coordinates (2, 1) refer to a point that is 2 steps right and 1 step up from the origin.',
    xpReward: 15,
  },

  // SCREEN 2: QUESTION 2 - Place point at (-1, 3)
  {
    id: 'q2',
    type: 'question',
    questionText: 'Now place a point at the coordinates (-1, 3).',
    questionType: 'place-point',
    gridSize: 380,
    gridRange: 5,
    targetCoordinates: { x: -1, y: 3 },
    explanation: 'The coordinates (-1, 3) refer to a point that is 1 step left and 3 steps up from the origin.',
    xpReward: 15,
  },

  // SCREEN 3: EDUCATIONAL INTERLUDE - Introducing Separation
  {
    id: 'exp1',
    type: 'explanatory',
    text: 'The positions of points can be described with coordinates.',
    bottomText: 'The difference in x-coordinates is their horizontal separation and the difference in y-coordinates is their vertical separation.',
    gridSize: 380,
    gridRange: 5,
    displayPoints: [
      { id: 'p1', x: -2, y: 1, color: '#f97316', showLabel: true, labelOffset: { x: -55, y: -15 } },
      { id: 'p2', x: 3, y: 4, color: '#f97316', showLabel: true, labelOffset: { x: 15, y: -15 } },
    ],
    displaySegments: [
      { from: 'p1', to: 'p2', type: 'l-shaped', color: '#3b82f6', showHorizontalLabel: true, showVerticalLabel: true },
    ],
  },

  // SCREEN 4: QUESTION 3 - Separation h=2, v=1 from (-4, 2)
  {
    id: 'q3',
    type: 'question',
    questionText: 'Place a point with a horizontal separation of 2 and a vertical separation of 1 from the highlighted point.',
    questionType: 'separation',
    gridSize: 380,
    gridRange: 5,
    referencePoint: { x: -4, y: 2 },
    targetSeparation: { horizontal: 2, vertical: 1 },
    explanation: 'For a horizontal separation of 2, the x-coordinate must be -6 or -2, but -6 is not within the plottable area. For a vertical separation of 1, the y-coordinate must be 1 or 3. Both (-2, 1) and (-2, 3) are valid solutions.',
    xpReward: 15,
  },

  // SCREEN 5: QUESTION 4 - Separation h=1, v=4 from (-2, -2)
  {
    id: 'q4',
    type: 'question',
    questionText: 'Place a point with a horizontal separation of 1 and a vertical separation of 4.',
    questionType: 'separation',
    gridSize: 380,
    gridRange: 5,
    referencePoint: { x: -2, y: -2 },
    targetSeparation: { horizontal: 1, vertical: 4 },
    explanation: 'For a horizontal separation of 1, the x-coordinate must be -1 or -3. For a vertical separation of 4, the y-coordinate must be 2, as -6 is not within the plottable area.',
    xpReward: 15,
  },

  // SCREEN 6: QUESTION 5 - Separation h=3, v=3 from (3, -1)
  {
    id: 'q5',
    type: 'question',
    questionText: 'Place a point with a horizontal separation of 3 and a vertical separation of 3.',
    questionType: 'separation',
    gridSize: 380,
    gridRange: 5,
    referencePoint: { x: 3, y: -1 },
    targetSeparation: { horizontal: 3, vertical: 3 },
    explanation: 'For a horizontal separation of 3, the x-coordinate must be 0 or 6, while a vertical separation of 3 allows for a y-coordinate of 2 or -4. Both (0, 2) and (0, -4) are solutions within the plottable area.',
    xpReward: 15,
  },

  // SCREEN 7: OUTRO
  {
    id: 'outro',
    type: 'outro',
    title: 'Coming up...',
    text: "In this course, we'll develop tools to find the straight line distance between points in the plane.",
    gridSize: 380,
    gridRange: 5,
    displayPoints: [
      { id: 'a', x: -2, y: -1, color: '#f97316', showLabel: true, labelOffset: { x: -55, y: 15 } },
      { id: 'b', x: 3, y: 3, color: '#f97316', showLabel: true, labelOffset: { x: 15, y: -15 } },
    ],
    displaySegments: [
      { from: 'a', to: 'b', type: 'direct', dashed: true },
    ],
  },

  // SCREEN 8: SKILL CHECK INTRO
  {
    id: 'skill-check-intro',
    type: 'skill-check-intro',
    title: 'Skill Check',
  },

  // SCREEN 9: SKILL CHECK Q1 - Separation h=3, v=5 from (1, -1)
  {
    id: 'skill-q1',
    type: 'question',
    questionText: 'Place a point with a horizontal separation of 3 and a vertical separation of 5.',
    questionType: 'separation',
    gridSize: 380,
    gridRange: 5,
    referencePoint: { x: 1, y: -1 },
    targetSeparation: { horizontal: 3, vertical: 5 },
    explanation: 'For a horizontal separation of 3, the x-coordinate must be 4 or -2. For a vertical separation of 5, the y-coordinate must be 4.',
    xpReward: 15,
  },

  // SCREEN 10: SKILL CHECK Q2 - Separation h=3, v=2 from (1, 1)
  {
    id: 'skill-q2',
    type: 'question',
    questionText: 'Place a point with a horizontal separation of 3 and a vertical separation of 2.',
    questionType: 'separation',
    gridSize: 380,
    gridRange: 5,
    referencePoint: { x: 1, y: 1 },
    targetSeparation: { horizontal: 3, vertical: 2 },
    explanation: 'For a horizontal separation of 3, the x-coordinate must be 4 or -2. For a vertical separation of 2, the y-coordinate must be 3 or -1.',
    xpReward: 15,
  },

  // SCREEN 11: SKILL CHECK Q3 - Separation h=5, v=4 from (-1, 0)
  {
    id: 'skill-q3',
    type: 'question',
    questionText: 'Place a point with a horizontal separation of 5 and a vertical separation of 4.',
    questionType: 'separation',
    gridSize: 380,
    gridRange: 5,
    referencePoint: { x: -1, y: 0 },
    targetSeparation: { horizontal: 5, vertical: 4 },
    explanation: 'For a horizontal separation of 5, the x-coordinate must be 4. For a vertical separation of 4, the y-coordinate must be 4 or -4.',
    xpReward: 15,
  },

  // SCREEN 12: LESSON COMPLETE
  {
    id: 'complete',
    type: 'complete',
    title: 'Lesson Complete!',
    subtitle: 'You can re-earn XP from this lesson by redoing it a day after completion.',
  },
];

const SKILL_CHECK_QUESTION_IDS = ['skill-q1', 'skill-q2', 'skill-q3'];

interface LessonFourProps {
  onClose?: () => void;
}

const LessonFour: React.FC<LessonFourProps> = ({ onClose }) => {
  // ============ STATE ============
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [visibleNodeIndices, setVisibleNodeIndices] = useState<number[]>([0]);
  const [pointPositions, setPointPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [completedQuestions, setCompletedQuestions] = useState<Set<string>>(new Set());
  const [incorrectAttempts, setIncorrectAttempts] = useState<Record<string, number>>({});
  const [whyExpandedStates, setWhyExpandedStates] = useState<Record<string, boolean>>({});
  const [streakCount, setStreakCount] = useState(0);
  const [totalXP, setTotalXP] = useState(0);
  const [inSkillCheckMode, setInSkillCheckMode] = useState(false);
  const [showExplanationPopup, setShowExplanationPopup] = useState(false);
  const [explanationPopupContent, setExplanationPopupContent] = useState<{ question: string; explanation: string } | null>(null);
  const [answerRevealed, setAnswerRevealed] = useState<Record<string, boolean>>({});
  const [answeredCorrectly, setAnsweredCorrectly] = useState<Record<string, boolean>>({});
  const [pointStates, setPointStates] = useState<Record<string, 'default' | 'correct' | 'incorrect'>>({});

  const contentRef = useRef<HTMLDivElement>(null);

  // ============ COMPUTED VALUES ============
  const currentNode = useMemo(() => LESSON_NODES[currentNodeIndex], [currentNodeIndex]);

  const visibleNodes = useMemo(() => {
    return visibleNodeIndices.map(i => ({ index: i, node: LESSON_NODES[i] }));
  }, [visibleNodeIndices]);

  const lessonProgress = useMemo(() => {
    const skillCheckIntroIndex = LESSON_NODES.findIndex(n => n.type === 'skill-check-intro');
    if (skillCheckIntroIndex === -1) return 0;
    if (currentNodeIndex >= skillCheckIntroIndex) return 1;
    return currentNodeIndex / skillCheckIntroIndex;
  }, [currentNodeIndex]);

  const masteryQuestions = useMemo<MasteryQuestion[]>(() => {
    const currentNodeId = LESSON_NODES[currentNodeIndex]?.id;
    return SKILL_CHECK_QUESTION_IDS.map(id => {
      if (completedQuestions.has(id)) {
        const isCorrect = answeredCorrectly[id] ?? false;
        return { state: isCorrect ? 'correct' : 'incorrect' } as MasteryQuestion;
      } else if (currentNodeId === id) {
        return { state: 'current' } as MasteryQuestion;
      } else {
        return { state: 'pending' } as MasteryQuestion;
      }
    });
  }, [currentNodeIndex, completedQuestions, answeredCorrectly]);

  const footerState = useMemo<FooterState>(() => {
    if (!currentNode) return 'default';
    if (currentNode.type !== 'question') return 'default';

    const nodeId = currentNode.id;
    if (completedQuestions.has(nodeId)) return 'correct';
    if (answerRevealed[nodeId]) return 'answer-revealed';
    if ((incorrectAttempts[nodeId] || 0) > 0) return 'incorrect';
    return 'default';
  }, [currentNode, completedQuestions, answerRevealed, incorrectAttempts]);

  const footerButtonText = useMemo(() => {
    if (!currentNode) return 'Continue';
    if (currentNode.type === 'question') return 'Check';
    return 'Continue';
  }, [currentNode]);

  const isLastQuestion = useMemo(() => {
    const completeIdx = LESSON_NODES.findIndex(n => n.type === 'complete');
    return currentNodeIndex === completeIdx - 1;
  }, [currentNodeIndex]);

  const currentXPReward = useMemo(() => currentNode?.xpReward ?? 15, [currentNode]);

  // ============ HELPER FUNCTIONS ============
  const checkAnswer = useCallback((node: LessonNode): boolean => {
    const nodeId = node.id;
    const userPos = pointPositions[nodeId] ?? { x: 0, y: 0 };
    const range = node.gridRange ?? 5;

    if (node.questionType === 'place-point' && node.targetCoordinates) {
      return userPos.x === node.targetCoordinates.x && userPos.y === node.targetCoordinates.y;
    }

    if (node.questionType === 'separation' && node.referencePoint && node.targetSeparation) {
      const hDist = Math.abs(userPos.x - node.referencePoint.x);
      const vDist = Math.abs(userPos.y - node.referencePoint.y);

      if (userPos.x < -range || userPos.x > range || userPos.y < -range || userPos.y > range) {
        return false;
      }

      return hDist === node.targetSeparation.horizontal && vDist === node.targetSeparation.vertical;
    }

    return false;
  }, [pointPositions]);

  const getQuestionPoints = useCallback((node: LessonNode): GridPoint[] => {
    const points: GridPoint[] = [];
    const nodeId = node.id;
    const completed = completedQuestions.has(nodeId);
    const pointState = pointStates[nodeId];

    if (node.questionType === 'separation' && node.referencePoint) {
      points.push({
        id: 'ref',
        x: node.referencePoint.x,
        y: node.referencePoint.y,
        color: '#f97316',
        showLabel: true,
        labelOffset: { x: -55, y: -15 },
      });
    }

    const userPos = pointPositions[nodeId];
    points.push({
      id: 'user',
      x: userPos?.x ?? 0,
      y: userPos?.y ?? 0,
      draggable: !completed,
      showLabel: true,
      state: completed ? pointState : undefined,
    });

    return points;
  }, [completedQuestions, pointPositions, pointStates]);

  const getQuestionSegments = useCallback((node: LessonNode): GridSegment[] => {
    if (node.questionType !== 'separation') return [];
    if (!node.referencePoint) return [];

    const nodeId = node.id;
    const completed = completedQuestions.has(nodeId);
    const userPos = pointPositions[nodeId];

    if (!userPos && !completed) return [];

    return [{
      from: 'ref',
      to: 'user',
      type: 'l-shaped',
      color: '#3b82f6',
      showHorizontalLabel: true,
      showVerticalLabel: true,
    }];
  }, [completedQuestions, pointPositions]);

  // ============ EVENT HANDLERS ============
  const handlePointMove = useCallback((nodeId: string, pointId: string, position: { x: number; y: number }) => {
    if (pointId === 'user') {
      setPointPositions(prev => ({
        ...prev,
        [nodeId]: position,
      }));
    }
  }, []);

  const advanceToNextNode = useCallback(() => {
    const nextIdx = currentNodeIndex + 1;
    if (nextIdx >= LESSON_NODES.length) return;

    const nextNode = LESSON_NODES[nextIdx];
    if (nextNode.type === 'skill-check-intro') {
      setInSkillCheckMode(true);
    }

    if (inSkillCheckMode || nextNode.type === 'skill-check-intro') {
      setVisibleNodeIndices([nextIdx]);
    } else {
      setVisibleNodeIndices(prev => [...prev, nextIdx]);
    }

    setCurrentNodeIndex(nextIdx);

    setTimeout(() => {
      const element = document.getElementById(`node-${nextIdx}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [currentNodeIndex, inSkillCheckMode]);

  const checkCurrentAnswer = useCallback(() => {
    const node = currentNode;
    if (!node || node.type !== 'question') return;

    const isCorrect = checkAnswer(node);

    setPointStates(prev => ({
      ...prev,
      [node.id]: isCorrect ? 'correct' : 'incorrect',
    }));

    if (isCorrect) {
      setCompletedQuestions(prev => new Set([...prev, node.id]));
      setAnsweredCorrectly(prev => ({ ...prev, [node.id]: true }));
      setTotalXP(prev => prev + (node.xpReward ?? 15));
      setStreakCount(prev => prev + 1);
      setIncorrectAttempts(prev => ({ ...prev, [node.id]: 0 }));
    } else {
      setIncorrectAttempts(prev => ({
        ...prev,
        [node.id]: (prev[node.id] || 0) + 1,
      }));
      setStreakCount(0);
    }
  }, [currentNode, checkAnswer]);

  const handleContinue = useCallback(() => {
    const node = currentNode;
    if (!node) return;

    if (node.type === 'question' && !completedQuestions.has(node.id)) {
      checkCurrentAnswer();
      return;
    }

    advanceToNextNode();
  }, [currentNode, completedQuestions, checkCurrentAnswer, advanceToNextNode]);

  const handleWhy = useCallback(() => {
    const node = currentNode;
    if (!node) return;

    if (inSkillCheckMode) {
      setExplanationPopupContent({
        question: node.questionText ?? '',
        explanation: node.explanation ?? '',
      });
      setShowExplanationPopup(true);
    } else {
      setWhyExpandedStates(prev => ({
        ...prev,
        [node.id]: !prev[node.id],
      }));
    }
  }, [currentNode, inSkillCheckMode]);

  const handleTryAgain = useCallback(() => {
    const node = currentNode;
    if (!node) return;

    setIncorrectAttempts(prev => ({ ...prev, [node.id]: 0 }));
    setPointPositions(prev => {
      const updated = { ...prev };
      delete updated[node.id];
      return updated;
    });
    setPointStates(prev => {
      const updated = { ...prev };
      delete updated[node.id];
      return updated;
    });
  }, [currentNode]);

  const handleSeeAnswer = useCallback(() => {
    const node = currentNode;
    if (!node) return;

    setAnswerRevealed(prev => ({ ...prev, [node.id]: true }));
    setAnsweredCorrectly(prev => ({ ...prev, [node.id]: false }));
    setIncorrectAttempts(prev => ({ ...prev, [node.id]: 0 }));

    if (node.questionType === 'place-point' && node.targetCoordinates) {
      setPointPositions(prev => ({
        ...prev,
        [node.id]: { ...node.targetCoordinates! },
      }));
    }

    if (node.questionType === 'separation' && node.referencePoint && node.targetSeparation) {
      const ref = node.referencePoint;
      const h = node.targetSeparation.horizontal;
      const v = node.targetSeparation.vertical;
      const range = node.gridRange ?? 5;

      let x = ref.x + h;
      let y = ref.y + v;

      if (x > range) x = ref.x - h;
      if (y > range) y = ref.y - v;
      if (x < -range) x = ref.x + h;
      if (y < -range) y = ref.y + v;

      setPointPositions(prev => ({
        ...prev,
        [node.id]: { x, y },
      }));
    }

    setPointStates(prev => ({ ...prev, [node.id]: 'correct' }));
  }, [currentNode]);

  const handleSkipExplanation = useCallback(() => {
    const node = currentNode;
    if (node) {
      setCompletedQuestions(prev => new Set([...prev, node.id]));
    }
    advanceToNextNode();
  }, [currentNode, advanceToNextNode]);

  const handleCloseExplanationPopup = useCallback(() => {
    setShowExplanationPopup(false);
    setExplanationPopupContent(null);
  }, []);

  // ============ RENDER NODE CONTENT ============
  const renderNode = useCallback((node: LessonNode, index: number) => {
    switch (node.type) {
      case 'question':
        return (
          <div key={node.id} id={`node-${index}`} className="py-8">
            <p className="text-lg text-gray-800 mb-6 text-center px-4">
              {node.questionText}
            </p>
            <div className="flex justify-center">
              <CoordinateGrid
                size={node.gridSize ?? 380}
                gridRange={node.gridRange ?? 5}
                points={getQuestionPoints(node)}
                segments={getQuestionSegments(node)}
                onPointMove={(pointId, position) => handlePointMove(node.id, pointId, position)}
              />
            </div>
            {/* Explanation when expanded */}
            {whyExpandedStates[node.id] && node.explanation && (
              <div className="mt-6 mx-4 p-4 bg-blue-50 rounded-xl">
                <p className="text-gray-700">{node.explanation}</p>
              </div>
            )}
          </div>
        );

      case 'explanatory':
        return (
          <div key={node.id} id={`node-${index}`} className="py-8 text-center">
            {node.text && (
              <p className="text-lg text-gray-800 mb-6 px-4">{node.text}</p>
            )}
            {node.displayPoints && (
              <div className="flex justify-center">
                <CoordinateGrid
                  size={node.gridSize ?? 380}
                  gridRange={node.gridRange ?? 5}
                  points={node.displayPoints}
                  segments={node.displaySegments}
                />
              </div>
            )}
            {node.bottomText && (
              <p className="text-base text-gray-600 mt-6 px-4">{node.bottomText}</p>
            )}
          </div>
        );

      case 'outro':
        return (
          <div key={node.id} id={`node-${index}`} className="py-8 text-center">
            {node.title && (
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{node.title}</h2>
            )}
            {node.text && (
              <p className="text-lg text-gray-600 mb-6 px-4">{node.text}</p>
            )}
            {node.displayPoints && (
              <div className="flex justify-center">
                <CoordinateGrid
                  size={node.gridSize ?? 380}
                  gridRange={node.gridRange ?? 5}
                  points={node.displayPoints}
                  segments={node.displaySegments}
                />
              </div>
            )}
          </div>
        );

      case 'skill-check-intro':
        return (
          <div key={node.id} id={`node-${index}`} className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <svg className="w-10 h-10 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{node.title}</h2>
            <p className="text-gray-500 mt-2">Test your knowledge</p>
          </div>
        );

      case 'complete':
        return (
          <div key={node.id} id={`node-${index}`} className="py-16 text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{node.title}</h2>
            {node.subtitle && (
              <p className="text-gray-500 px-4">{node.subtitle}</p>
            )}
            <div className="mt-8 flex items-center justify-center gap-2 text-2xl font-bold text-emerald-500">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L9.5 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5 6-4.5h-7.5L12 2z"/>
              </svg>
              <span>+{totalXP} XP</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [currentNodeIndex, getQuestionPoints, getQuestionSegments, handlePointMove, whyExpandedStates, totalXP]);

  // ============ RENDER ============
  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <LessonHeader
        progress={lessonProgress}
        masteryQuestions={inSkillCheckMode ? masteryQuestions : undefined}
        streakCount={streakCount}
        onClose={onClose}
      />

      {/* Content */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto"
      >
        <div className="max-w-2xl mx-auto">
          {visibleNodes.map(({ index, node }) => renderNode(node, index))}
        </div>
      </div>

      {/* Footer */}
      {currentNode?.type !== 'complete' && (
        <ExerciseFooter
          state={footerState}
          buttonText={footerButtonText}
          buttonEnabled={true}
          xpAmount={currentXPReward}
          isLastQuestion={isLastQuestion}
          onContinue={handleContinue}
          onWhy={handleWhy}
          onTryAgain={handleTryAgain}
          onSeeAnswer={handleSeeAnswer}
          onSkipExplanation={handleSkipExplanation}
        />
      )}

      {/* Explanation Popup (for skill check mode) */}
      {showExplanationPopup && explanationPopupContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Explanation</h3>
            <p className="text-gray-600 mb-4">{explanationPopupContent.question}</p>
            <p className="text-gray-700 mb-6">{explanationPopupContent.explanation}</p>
            <button
              className="w-full py-3 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-600 transition-colors"
              onClick={handleCloseExplanationPopup}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonFour;
